import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import styles from "../../styles/QuickNav.module.css";

// Homepage quick links: the most visited categories (side menu icons) as one row of cards that scrolls
// sideways, with arrow buttons at the ends. Computers and tablets only: phones have the category chips under
// the header (CategoryChips.js).
// Order = category page visitors, GA4 2 Aug - 30 Oct 2026 (reports/top-engagement-pages.md): Dress up 2,162,
// Exclusive 1,018, Make Up 870, Princess 861, Celebrity 757, Wedding 564, Halloween 506, Toca Boca 498,
// Cooking 447. Christmas sits after Halloween for the season. The rest have no data yet: K-Pop and Aesthetic
// first (their games hold players longest in the same report), View All last.
const CATEGORIES = [
  { name: "Dress up", icon: "dressup", url: "/categories/dress-up-games" },
  { name: "Exclusive", icon: "exclusive", url: "/categories/exclusive-games" },
  { name: "Make Up", icon: "makeup", url: "/categories/makeover-games" },
  { name: "Princess", icon: "princess", url: "/categories/princess-games" },
  { name: "Celebrity", icon: "celebrity", url: "/categories/celebrity-games" },
  { name: "Wedding", icon: "wedding", url: "/categories/wedding-games" },
  { name: "Halloween", icon: "halloween", url: "/categories/halloween-games" },
  { name: "Christmas", icon: "christmas", url: "/categories/christmas-games" },
  { name: "Toca Boca", icon: "toca-boca", url: "/categories/toca-boca" },
  { name: "Cooking", icon: "cooking", url: "/categories/cooking-games" },
  { name: "K-Pop", icon: "kpop", url: "/categories/kpop-dress-up-games" },
  { name: "Aesthetic", icon: "aesthetic", url: "/categories/aesthetic-dress-up-games" },
  { name: "Frozen", icon: "frozen", url: "/categories/frozen-games" },
  { name: "Monster High", icon: "monster-high", url: "/categories/monster-high-games" },
  { name: "Brainrot", icon: "brainrot", url: "/categories/brainrot-games" },
  { name: "Decoration", icon: "decoration", url: "/categories/decoration-games" },
  { name: "Doctor", icon: "doctor", url: "/categories/doctor-games" },
  { name: "Winx club", icon: "winx", url: "/categories/winx-club-games" },
  { name: "Shopaholic", icon: "shopaholic", url: "/categories/shopaholic-games" },
  { name: "FNF", icon: "friday-night-funkin", url: "/categories/friday-night-funkin" },
  { name: "My Dolphin Show", icon: "dolphin", url: "/categories/my-dolphin-show" },
  { name: "View All", icon: "all-tag", url: "/all-categories" },
];

const Arrow = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === "left" ? "M15 5 L8 12 L15 19" : "M9 5 L16 12 L9 19"} />
  </svg>
);

function Item({ item }) {
  return (
    <Link href={item.url} prefetch={false} className={styles.item}>
      <span className={styles.icon}>
        <img src={`/assets/sidenav/v2/${item.icon}.svg`} alt="" width="26" height="26" />
      </span>
      <span className={styles.name}>{item.name}</span>
    </Link>
  );
}

export default function QuickNav() {
  const row = useRef(null);
  // which ends still have more to scroll to (the arrow and edge fade only show there)
  const [ends, setEnds] = useState({ left: false, right: true });

  useEffect(() => {
    const el = row.current;
    const update = () => setEnds({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, []);

  // one tap moves most of a screen width, so a few taps reach the end
  const scrollBy = (dir) => row.current.scrollBy({ left: dir * row.current.clientWidth * 0.75, behavior: "smooth" });

  return (
    <nav className={`${styles.wrap} ${ends.left ? styles.moreLeft : ""} ${ends.right ? styles.moreRight : ""}`} aria-label="Popular categories">
      <button type="button" className={`${styles.arrow} ${styles.arrowLeft}`} aria-label="Scroll left" tabIndex={ends.left ? 0 : -1} onClick={() => scrollBy(-1)}>
        <Arrow dir="left" />
      </button>
      <div className={styles.row} ref={row}>
        {CATEGORIES.map((item) => <Item key={item.url} item={item} />)}
      </div>
      <button type="button" className={`${styles.arrow} ${styles.arrowRight}`} aria-label="Scroll right" tabIndex={ends.right ? 0 : -1} onClick={() => scrollBy(1)}>
        <Arrow dir="right" />
      </button>
    </nav>
  );
}
